import React from 'react';
import './paperwork.css';
import { COMPANY } from '../../constants/company';

const Header = () => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="20" fill="#1F0068" />
        <path d="M12 22 L18 28 L28 14" fill="none" stroke="#FF5C39" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="leading-tight">
        <div className="font-bold text-[15px] text-[#1F0068]">{COMPANY.shortName}</div>
        <div className="text-[9px] tracking-[0.22em] text-[#FF5C39] uppercase font-semibold">Cancellation Notice</div>
      </div>
    </div>
    <div className="text-right text-[10px] text-[#1F0068]/70 leading-relaxed">
      <div>{COMPANY.phone}</div>
      <div>{COMPANY.email}</div>
      <div>{COMPANY.registeredOffice.full}</div>
    </div>
  </div>
);

const CancellationLetter = ({ policy }) => {
  const c = policy.cancellation || {};
  const confirmed = !!c.confirmed;
  const fullName = `${policy.title || ''} ${policy.firstName} ${policy.lastName}`.trim();
  const cancelDate = c.cancelledAt ? new Date(c.cancelledAt) : new Date();
  const paymentDate = c.paymentDate || policy.paymentDue;
  const amount = Number(c.amount || 0);

  return (
    <div className="a4-doc">
      <div className="a4-brand-strip" />
      <Header />

      <div className="mt-8 grid grid-cols-[1fr_auto] gap-4 items-start">
        <div className="text-[11px] text-[#1F0068]/80">
          <div className="font-bold text-[#1F0068]">{fullName}</div>
          <div>{policy.address1}</div>
          {policy.address2 && <div>{policy.address2}</div>}
          <div>{policy.city}</div>
          <div>{policy.postcode}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div className="bg-[#FFF6EB] rounded-lg p-2 text-center">
            <div className="uppercase tracking-widest text-[#1F0068]/60 font-bold text-[9px]">Cancellation Date</div>
            <div className="font-bold text-[#1F0068]">{cancelDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
          <div className="bg-[#FFEDD6] rounded-lg p-2 text-center">
            <div className="uppercase tracking-widest text-[#1F0068]/60 font-bold text-[9px]">Plan Number</div>
            <div className="font-bold text-[#1F0068]">{policy.planNumber}</div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[13px] text-[#1F0068]/85">Dear {policy.firstName},</div>
        <h1 className="mt-2 text-3xl font-extrabold leading-tight">{confirmed ? <>Your <span className="text-[#FF5C39]">Maintenance Cover Plan</span> has been cancelled</> : <>Cancellation <span className="text-[#FF5C39]">request received</span></>}</h1>
        <p className="mt-4 text-[12px] leading-relaxed text-[#1F0068]/85">
          {confirmed
            ? <>Further to your request, we can confirm that your Maintenance Cover Plan with {COMPANY.name} has been cancelled with effect from {cancelDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}. From this date, no further cover, repairs or replacements will be provided under plan number {policy.planNumber}.</>
            : <>We have received your request to cancel your Maintenance Cover Plan with {COMPANY.name}. A cancellation / admin charge of <b>£{Number(c.amount || 0).toFixed(2)}</b> is payable. Your cancellation will only be completed and confirmed once this amount has been received and cleared. Until then, plan number <b>{policy.planNumber}</b> remains open on our records.</>}
        </p>
        <p className="mt-3 text-[12px] leading-relaxed text-[#1F0068]/85">
          In accordance with your Terms &amp; Conditions, a settlement of <b>£{amount.toFixed(2)}</b> is applicable as detailed in the accompanying Cancellation Invoice. Please arrange payment via Direct Bank Transfer using the details on the invoice, quoting the reference {policy.planNumber}.
        </p>
        {c.reason && (
          <p className="mt-3 text-[12px] leading-relaxed text-[#1F0068]/85">
            <b>Reason recorded:</b> {c.reason}
          </p>
        )}
      </div>

      <div className="mt-6 bg-[#FFF6EB] rounded-xl p-4 text-[11px] text-[#1F0068]/85">
        <div className="font-bold text-[#1F0068] mb-1">Summary of the cover you had</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><span className="text-[#1F0068]/60">Plan start:</span> <b>{new Date(policy.policyStart).toLocaleDateString('en-GB')}</b></div>
          <div><span className="text-[#1F0068]/60">Original payment date:</span> <b>{new Date(paymentDate).toLocaleDateString('en-GB')}</b></div>
          <div><span className="text-[#1F0068]/60">Cancellation date:</span> <b>{cancelDate.toLocaleDateString('en-GB')}</b></div>
          <div><span className="text-[#1F0068]/60">Cancellation amount:</span> <b>£{amount.toFixed(2)}</b></div>
        </div>
        <div className="mt-2"><span className="text-[#1F0068]/60">Products previously covered:</span> {policy.appliances.join(', ')}</div>
      </div>

      <div className="mt-6 bg-[#1F0068] text-white rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Useful Contact Details</div>
        <div className="grid grid-cols-2 mt-2 text-[12px]">
          <div><div className="text-white/70 text-[10px]">Helpline Number</div><div className="font-bold">{COMPANY.phone}</div></div>
          <div><div className="text-white/70 text-[10px]">Email</div><div className="font-bold">{COMPANY.email}</div></div>
        </div>
      </div>

      <div className="mt-6 text-[12px] text-[#1F0068]/85">
        <div>Yours sincerely,</div>
        <div className="mt-1" style={{ fontFamily: 'cursive', fontSize: '18px', color: '#1F0068' }}>Team {COMPANY.shortName}</div>
        <div className="text-[10px] text-[#1F0068]/60">Customer Service Team</div>
      </div>

      <div className="a4-footer">
        <div>{COMPANY.name} · {COMPANY.registeredOffice.full}</div>
        <div>Company No. {COMPANY.companyNumber} · Cancellation for Plan {policy.planNumber}</div>
      </div>
    </div>
  );
};

export default CancellationLetter;
